
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Download, FileText, Database, Code, BookOpen, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { JournalEntry } from "@/hooks/journal/types";
import { ExportFormat, exportEntries } from "@/utils/exportUtils";
import { useToast } from "@/components/ui/use-toast";
import { useSettings } from "@/context/SettingsContext";
import { getRpgThemeClasses } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entries: JournalEntry[];
  defaultFormat?: ExportFormat;
  allowDateFilter?: boolean;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  isOpen,
  onClose,
  entries,
  defaultFormat = 'pdf',
  allowDateFilter = false
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(defaultFormat);
  const [customFilename, setCustomFilename] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);
  const isMobile = useIsMobile();

  const formatOptions = [
    {
      value: 'pdf' as ExportFormat,
      label: enableRpgMode ? 'Scroll Archive (PDF)' : 'PDF Document',
      description: enableRpgMode ? 'Beautiful chronicle for sharing and printing' : 'Professional document for sharing and printing',
      icon: FileText,
      color: 'text-red-500'
    },
    {
      value: 'csv' as ExportFormat,
      label: enableRpgMode ? 'Data Tome (CSV)' : 'CSV Spreadsheet',
      description: enableRpgMode ? 'Structured data for analysis and tracking' : 'Spreadsheet format for data analysis',
      icon: Database,
      color: 'text-green-500'
    },
    {
      value: 'json' as ExportFormat,
      label: enableRpgMode ? 'Codex Archive (JSON)' : 'JSON Data',
      description: enableRpgMode ? 'Technical backup for scribes and developers' : 'Technical backup and data portability',
      icon: Code,
      color: 'text-blue-500'
    },
    {
      value: 'markdown' as ExportFormat,
      label: enableRpgMode ? 'Chronicle Text (Markdown)' : 'Markdown Document',
      description: enableRpgMode ? 'Readable text format for personal archives' : 'Text format for personal archives',
      icon: BookOpen,
      color: 'text-purple-500'
    }
  ];

  const getFilteredEntries = () => {
    if (!allowDateFilter || (!dateFrom && !dateTo)) {
      return entries;
    }

    return entries.filter(entry => {
      const entryDate = new Date(entry.created_at);
      const fromDate = dateFrom ? new Date(dateFrom.setHours(0, 0, 0, 0)) : null;
      const toDate = dateTo ? new Date(dateTo.setHours(23, 59, 59, 999)) : null;

      if (fromDate && entryDate < fromDate) return false;
      if (toDate && entryDate > toDate) return false;
      return true;
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const filteredEntries = getFilteredEntries();
      
      if (filteredEntries.length === 0) {
        toast({
          title: "No entries to export",
          description: "Please adjust your date filter or select entries to export.",
          variant: "destructive"
        });
        return;
      }

      const success = await exportEntries(
        filteredEntries,
        selectedFormat,
        customFilename || undefined,
        enableRpgMode
      );

      if (success) {
        toast({
          title: enableRpgMode ? "Archive created successfully!" : "Export completed!",
          description: enableRpgMode 
            ? `Your chronicle has been saved to ${selectedFormat.toUpperCase()} format.`
            : `${filteredEntries.length} journal entries exported to ${selectedFormat.toUpperCase()}.`
        });
        onClose();
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "There was an error exporting your journal entries. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const selectedOption = formatOptions.find(option => option.value === selectedFormat);
  const filteredEntries = getFilteredEntries();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "mx-4 max-w-sm sm:max-w-[500px] max-h-[90vh] overflow-y-auto",
        enableRpgMode && rpgTheme.cardBackground
      )}>
        <DialogHeader className="relative">
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="absolute -top-2 -right-2 h-8 w-8 p-0 rounded-full"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </DialogClose>
          <DialogTitle className={cn(
            "text-lg sm:text-xl pr-8",
            enableRpgMode && rpgTheme.text
          )}>
            {enableRpgMode ? "Export Your Chronicle" : "Export Journal Entries"}
          </DialogTitle>
          <DialogDescription className={cn(
            "text-sm",
            enableRpgMode && rpgTheme.accent
          )}>
            {enableRpgMode 
              ? "Choose how you'd like to archive your adventure entries for safekeeping or sharing."
              : "Choose your preferred export format and customize the file settings."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Format Selection */}
          <div>
            <Label className={cn(
              "text-sm font-medium mb-3 block",
              enableRpgMode && rpgTheme.text
            )}>
              {enableRpgMode ? "Archive Format" : "Export Format"}
            </Label>
            <RadioGroup
              value={selectedFormat}
              onValueChange={(value) => setSelectedFormat(value as ExportFormat)}
              className="space-y-2"
            >
              {formatOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <div key={option.value} className="flex items-start space-x-2">
                    <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                    <Label
                      htmlFor={option.value}
                      className={cn(
                        "flex items-start gap-3 cursor-pointer flex-1 p-3 rounded-lg border transition-colors",
                        selectedFormat === option.value 
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50",
                        enableRpgMode && rpgTheme.text
                      )}
                    >
                      <IconComponent className={cn("h-4 w-4 mt-0.5 flex-shrink-0", option.color)} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className={cn(
                          "text-xs mt-1 opacity-70 leading-tight",
                          enableRpgMode && rpgTheme.accent
                        )}>
                          {option.description}
                        </div>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Date Range Filter */}
          {allowDateFilter && (
            <div className="space-y-3">
              <Label className={cn(
                "text-sm font-medium",
                enableRpgMode && rpgTheme.text
              )}>
                {enableRpgMode ? "Date Range (Optional)" : "Filter by Date Range (Optional)"}
              </Label>
              <div className={cn(
                "flex gap-2 items-center",
                isMobile && "flex-col items-stretch"
              )}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal min-h-[44px]",
                        isMobile ? "w-full" : "w-[140px]",
                        !dateFrom && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, "MMM dd") : "From"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateFrom}
                      onSelect={setDateFrom}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {!isMobile && <span className={cn(enableRpgMode && rpgTheme.text)}>to</span>}

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal min-h-[44px]",
                        isMobile ? "w-full" : "w-[140px]",
                        !dateTo && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, "MMM dd") : "To"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dateTo}
                      onSelect={setDateTo}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          {/* Custom Filename */}
          <div>
            <Label htmlFor="filename" className={cn(
              "text-sm font-medium",
              enableRpgMode && rpgTheme.text
            )}>
              {enableRpgMode ? "Chronicle Name (Optional)" : "Custom Filename (Optional)"}
            </Label>
            <Input
              id="filename"
              placeholder={enableRpgMode ? "my-adventure-chronicle" : "my-journal-export"}
              value={customFilename}
              onChange={(e) => setCustomFilename(e.target.value)}
              className="mt-1 min-h-[44px]"
            />
            <p className={cn(
              "text-xs mt-1 opacity-70",
              enableRpgMode && rpgTheme.accent
            )}>
              {enableRpgMode 
                ? "Leave empty for automatic naming with current date"
                : "Leave empty for automatic naming with current date"
              }
            </p>
          </div>

          {/* Export Summary */}
          <div className={cn(
            "p-3 rounded-lg bg-muted/50 border",
            enableRpgMode && "bg-amber-900/20 border-amber-600/30"
          )}>
            <div className={cn(
              "text-sm",
              enableRpgMode && rpgTheme.text
            )}>
              <div className="font-medium mb-1">
                {enableRpgMode ? "Archive Summary:" : "Export Summary:"}
              </div>
              <div className={cn(
                "text-xs space-y-1",
                enableRpgMode && rpgTheme.accent
              )}>
                <div>• {filteredEntries.length} {enableRpgMode ? "quest entries" : "journal entries"}</div>
                <div>• Format: {selectedOption?.label}</div>
                {allowDateFilter && (dateFrom || dateTo) && (
                  <div>
                    • Date range: {dateFrom ? format(dateFrom, "MMM dd, yyyy") : "Start"} - {dateTo ? format(dateTo, "MMM dd, yyyy") : "End"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className={cn(
          "gap-2",
          isMobile && "flex-col"
        )}>
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={isExporting}
            className={cn(isMobile && "w-full min-h-[44px]")}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={isExporting || filteredEntries.length === 0}
            className={cn(
              enableRpgMode && rpgTheme.button,
              isMobile && "w-full min-h-[44px]"
            )}
          >
            {isExporting ? (
              <>Exporting...</>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                {enableRpgMode ? "Create Archive" : "Export"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
